import { Link } from 'react-router-dom'
import AppDropdown from '../../../components/AppDropdown'
import AppPagination from '../../../components/AppPagination'
import { useAuth } from '../../../hooks/useAuth'
import { useUserListScreen } from './hooks/useUserListScreen'

function initialsFromName(name) {
  return String(name || 'NA')
    .split(' ')
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0])
    .join('')
    .toUpperCase()
}

function statusBadgeClasses(tone) {
  if (tone === 'success') return 'text-emerald-700'
  if (tone === 'warning') return 'text-amber-700'
  if (tone === 'danger') return 'text-error font-bold'
  return 'text-outline'
}

function statusDotClasses(tone) {
  if (tone === 'success') return 'bg-emerald-500'
  if (tone === 'warning') return 'bg-amber-500'
  if (tone === 'danger') return 'bg-error'
  return 'bg-outline'
}

function scopeBadgeClasses(scope) {
  if (scope === 'ALL') return 'bg-emerald-500/10 text-emerald-700 border-emerald-500/20'
  if (scope === 'TEAM') return 'bg-sky-500/10 text-sky-700 border-sky-500/20'
  return 'bg-violet-500/10 text-violet-700 border-violet-500/20'
}

export default function UserDirectoryScreen() {
  const { state, actions } = useUserListScreen()
  const { hasPrivilege } = useAuth()

  return (
    <div className="mx-auto flex w-full max-w-[1500px] flex-col gap-7 pb-8">
      <section className="flex flex-col gap-5 xl:flex-row xl:items-start xl:justify-between">
        <div>
          <h1 className="text-4xl font-extrabold tracking-[-0.02em] text-on-surface">User Inventory</h1>
          <p className="mt-2 text-base text-on-surface-variant">
            Manage and monitor {state.totalCount || state.rows.length} active system identities.
          </p>
        </div>

        <div className="flex items-center gap-3">
          {hasPrivilege('UserAdd') && (
            <Link
              to="/masters/users/entry"
              className="inline-flex items-center gap-1.5 rounded-xl bg-primary px-5 py-3 text-sm font-bold text-on-primary shadow-sm hover:opacity-90 transition-all"
            >
              <span className="material-symbols-outlined text-base">person_add</span>
              Add User
            </Link>
          )}
        </div>
      </section>

      {state.error ? (
        <div className="rounded-xl border border-error/30 bg-error-container px-4 py-3 text-sm text-error">{state.error}</div>
      ) : null}

      <section className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-4">
        <article className="rounded-[1.75rem] bg-surface-container-lowest px-6 py-5">
          <p className="text-xs font-black uppercase tracking-[0.14em] text-on-surface-variant">Active Users</p>
          <div className="mt-2 flex items-center justify-between">
            <p className="text-4xl font-black tracking-tighter text-primary">{state.stats.activeUsers}</p>
            <span className="material-symbols-outlined rounded-full bg-surface-container-low p-3 text-primary">person</span>
          </div>
        </article>

        <article className="rounded-[1.75rem] bg-surface-container-lowest px-6 py-5">
          <p className="text-xs font-black uppercase tracking-[0.14em] text-on-surface-variant">Contractors</p>
          <div className="mt-2 flex items-center justify-between">
            <p className="text-4xl font-black tracking-tighter text-on-surface">{state.stats.contractors}</p>
            <span className="material-symbols-outlined rounded-full bg-surface-container-low p-3 text-on-surface-variant">badge</span>
          </div>
        </article>

        <Link
          to="/approval/masters/list?module=users"
          className="rounded-[1.75rem] bg-surface-container-lowest px-6 py-5 transition hover:shadow-md cursor-pointer block"
        >
          <p className="text-xs font-black uppercase tracking-[0.14em] text-on-surface-variant">Pending Approval</p>
          <div className="mt-2 flex items-center justify-between">
            <p className="text-4xl font-black tracking-tighter text-error">{state.stats.pendingApproval}</p>
            <span className="material-symbols-outlined rounded-full bg-error/10 p-3 text-error">pending</span>
          </div>
        </Link>

        <article className="rounded-[1.75rem] bg-surface-container-lowest px-6 py-5">
          <p className="text-xs font-black uppercase tracking-[0.14em] text-on-surface-variant">Avg. Login Depth</p>
          <div className="mt-2 flex items-center justify-between">
            <p className="text-4xl font-black tracking-tighter text-on-surface">{state.stats.avgLoginDepth}%</p>
            <span className="material-symbols-outlined rounded-full bg-surface-container-low p-3 text-on-surface-variant">bolt</span>
          </div>
        </article>
      </section>

      <section className="rounded-[2rem] bg-surface-container-low px-6 py-6">
        <div className="grid grid-cols-1 gap-4 xl:grid-cols-5">
          <label className="grid gap-2">
            <span className="text-[11px] font-black uppercase tracking-[0.14em] text-on-surface-variant">Search</span>
            <input
              type="text"
              value={state.searchText}
              onChange={(event) => actions.setSearchText(event.target.value)}
              placeholder="Search users..."
              className="rounded-xl border-0 bg-surface-container-lowest px-4 py-3 text-sm text-on-surface outline-none placeholder:text-on-surface-variant"
            />
          </label>

          <label className="grid gap-2">
            <span className="text-[11px] font-black uppercase tracking-[0.14em] text-on-surface-variant">Role</span>
            <AppDropdown
              value={state.roleId}
              onChange={actions.setRoleId}
              placeholder="All Roles"
              triggerClassName="rounded-xl border-0 bg-surface-container-lowest px-4 py-3 text-sm text-on-surface"
              options={[
                { value: '', label: 'All Roles' },
                ...state.roleOptions.map((role) => ({
                  value: String(role.id),
                  label: role.name,
                })),
              ]}
            />
          </label>

          <label className="grid gap-2">
            <span className="text-[11px] font-black uppercase tracking-[0.14em] text-on-surface-variant">Department</span>
            <AppDropdown
              value={state.departmentId}
              onChange={actions.setDepartmentId}
              placeholder="All Departments"
              triggerClassName="rounded-xl border-0 bg-surface-container-lowest px-4 py-3 text-sm text-on-surface"
              options={[
                { value: '', label: 'All Departments' },
                ...state.departmentOptions.map((department) => ({
                  value: String(department.id),
                  label: department.name,
                })),
              ]}
            />
          </label>

          <label className="grid gap-2">
            <span className="text-[11px] font-black uppercase tracking-[0.14em] text-on-surface-variant">Company / Branch</span>
            <AppDropdown
              value={state.companyId}
              onChange={actions.setCompanyId}
              placeholder="All Companies"
              triggerClassName="rounded-xl border-0 bg-surface-container-lowest px-4 py-3 text-sm text-on-surface"
              options={[
                { value: '', label: 'All Companies' },
                ...state.companyOptions.map((company) => ({
                  value: String(company.id),
                  label: company.name,
                })),
              ]}
            />
          </label>

          <label className="grid gap-2">
            <span className="text-[11px] font-black uppercase tracking-[0.14em] text-on-surface-variant">Status</span>
            <AppDropdown
              value={state.statusFilter}
              onChange={actions.setStatusFilter}
              triggerClassName="rounded-xl border-0 bg-surface-container-lowest px-4 py-3 text-sm text-on-surface"
              options={[
                { value: 'active', label: 'Active Only' },
                { value: 'inactive', label: 'Archived / Inactive' },
              ]}
            />
          </label>
        </div>
      </section>

      <section className="overflow-hidden rounded-[2rem] bg-surface-container-lowest">
        <div className="grid grid-cols-12 bg-surface-container/40 px-8 py-4 text-[11px] font-black uppercase tracking-[0.14em] text-on-surface-variant">
          <div className="col-span-1">Employee ID</div>
          <div className="col-span-3">Name &amp; Identity</div>
          <div className="col-span-2">Org Details</div>
          <div className="col-span-2">Role</div>
          <div className="col-span-1">Data Scope</div>
          <div className="col-span-1">Status</div>
          <div className="col-span-2 text-right">Reporting To</div>
          {/* <div className="col-span-0.5 text-right">Actions</div> */}
        </div>

        {state.isLoading ? (
          <div className="px-8 py-14 text-center text-sm text-on-surface-variant">Loading users...</div>
        ) : state.rows.length === 0 ? (
          <div className="px-8 py-14 text-center text-sm text-on-surface-variant">No users found for selected filters.</div>
        ) : (
          state.rows.map((row) => (
            <Link
              key={row.id}
              to={`/masters/users/entry?id=${row.id}`}
              className="grid grid-cols-12 items-center border-t border-outline-variant/20 px-8 py-5 transition-colors hover:bg-surface-container-low"
            >
              <div className="col-span-1">
                <p className="text-sm font-black text-primary">{row.userCode}</p>
              </div>

              <div className="col-span-3 flex items-center gap-3">
                {row.photoUrl ? (
                  <img src={row.photoUrl} alt={row.name} className="h-11 w-11 rounded-full object-cover" />
                ) : (
                  <div className="flex h-11 w-11 items-center justify-center rounded-full bg-primary-fixed text-primary text-xl font-bold">
                    {initialsFromName(row.name)}
                  </div>
                )}
                <div>
                  <p className="text-base font-bold leading-tight text-on-surface">{row.name}</p>
                  <p className="text-sm text-on-surface-variant">{row.email}</p>
                </div>
              </div>

              <div className="col-span-2 space-y-1 overflow-hidden pr-2">
                <div className="flex items-center gap-1.5" title={row.companyName}>
                  <span className="material-symbols-outlined text-xs text-primary flex-shrink-0">domain</span>
                  <span className="truncate text-xs font-bold text-on-surface">
                    {row.companyName || '-'}
                  </span>
                </div>
                {row.branchName && row.branchName !== '-' ? (
                  <div className="flex items-center gap-1.5" title={row.branchName}>
                    <span className="material-symbols-outlined text-xs text-secondary flex-shrink-0">apartment</span>
                    <span className="truncate text-[11px] text-on-surface-variant">
                      {row.branchName}
                    </span>
                  </div>
                ) : null}
                {row.departmentName && row.departmentName !== '-' ? (
                  <div className="flex items-center gap-1.5" title={row.departmentName}>
                    <span className="material-symbols-outlined text-xs text-tertiary flex-shrink-0">corporate_fare</span>
                    <span className="truncate text-[11px] text-on-surface-variant">
                      {row.departmentName}
                    </span>
                  </div>
                ) : null}
              </div>

              <div className="col-span-2">
                <div className="flex flex-wrap gap-1">
                  {row.roleName ? (
                    row.roleName.split(',').map((r, i) => (
                      <span
                        key={i}
                        className="inline-flex items-center rounded-md bg-secondary-container/80 px-2 py-0.5 text-xs font-semibold text-on-secondary-container"
                      >
                        {r.trim()}
                      </span>
                    ))
                  ) : (
                    <span className="text-xs text-on-surface-variant">-</span>
                  )}
                </div>
              </div>

              <div className="col-span-1">
                <span
                  className={`inline-flex items-center gap-1 rounded-md border px-2 py-0.5 text-[11px] font-bold uppercase tracking-wider ${scopeBadgeClasses(
                    row.dataScope,
                  )}`}
                >
                  <span className="material-symbols-outlined text-[13px]">
                    {row.dataScope === 'ALL' ? 'domain_verification' : row.dataScope === 'TEAM' ? 'diversity_3' : 'person'}
                  </span>
                  {row.dataScope}
                </span>
              </div>

              <div className="col-span-1">
                <span className={`inline-flex items-center gap-1.5 text-sm font-semibold ${statusBadgeClasses(row.statusTone)}`}>
                  <span className={`h-2 w-2 rounded-full ${statusDotClasses(row.statusTone)}`} />
                  {row.statusLabel}
                </span>
              </div>

              <div className="col-span-2 flex justify-end gap-2">
                {row.reportingManagerPhotoUrl ? (
                  <img
                    src={row.reportingManagerPhotoUrl}
                    alt={row.reportingManagerName}
                    className="h-8 w-8 rounded-full object-cover"
                  />
                ) : (
                  <div className="flex h-8 w-8 items-center justify-center rounded-full bg-surface-container-high text-xs font-bold text-on-surface-variant">
                    {row.reportingManagerInitials || 'NA'}
                  </div>
                )}
                <p className="line-clamp-2 text-sm text-on-surface">{row.reportingManagerName}</p>
              </div>

              {/* <div className="col-span-1 flex justify-end text-on-surface-variant">
                <span className="material-symbols-outlined">more_vert</span>
              </div> */}
            </Link>
          ))
        )}

        <div className="flex flex-col gap-3 border-t border-outline-variant/30 bg-surface-container-low/30 px-8 py-5 sm:flex-row sm:items-center sm:justify-between">
          <span className="text-sm text-on-surface-variant">
            Showing {state.rangeStart} to {state.rangeEnd} of {state.totalCount} identities
          </span>

          <AppPagination
            page={state.page}
            totalPages={state.totalPages}
            onPageChange={actions.goToPage}
            onPrevious={actions.goToPreviousPage}
            onNext={actions.goToNextPage}
            canPrevious={state.page > 1}
            canNext={Boolean(state.hasMore) || state.page < state.totalPages}
          />
        </div>
      </section>
    </div>
  )
}



